(() => {
  const root = document.documentElement;
  const body = document.body;

  if (!body || !body.classList.contains("sa-com-v6")) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const revealSelector = "[data-sa-v6-reveal]";
  const depthSelector = "[data-sa-v6-depth]";
  const glassSelector = "[data-sa-v6-glass]";
  let depthItems = [];
  let typeScrollItems = [];
  const sequenceStates = [];
  const lyricStates = [];
  const processStates = [];
  let parallaxFrame = 0;
  const saveData = Boolean(navigator.connection?.saveData);

  const revealAll = (scope = document) => {
    scope.querySelectorAll(revealSelector).forEach((item) => item.classList.add("is-visible"));
  };

  const initReveal = (scope = document) => {
    const items = [...scope.querySelectorAll(revealSelector)].filter((item) => !item.dataset.saV6Observed);

    if (!items.length) return;
    if (reducedMotion.matches || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.dataset.saV6Observed = "true";
        item.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -9% 0px", threshold: 0.12 }
    );

    items.forEach((item) => {
      item.dataset.saV6Observed = "true";
      observer.observe(item);
    });
  };

  const refreshDepthItems = () => {
    depthItems = [...document.querySelectorAll(depthSelector)];
    typeScrollItems = [...document.querySelectorAll("[data-sa-v6-type-scroll]")];
  };

  const paintParallax = () => {
    parallaxFrame = 0;
    if (body.classList.contains("sa-mobile-menu-open")) return;
    if (reducedMotion.matches) {
      depthItems.forEach((item) => item.style.setProperty("--sa-v6-depth-y", "0px"));
      typeScrollItems.forEach((item) => item.style.setProperty("--sa-v6-type-x", "0px"));
      return;
    }

    const viewportCenter = window.innerHeight / 2;
    depthItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -180 || rect.top > window.innerHeight + 180) return;
      const strength = Number.parseFloat(item.dataset.saV6Depth || "0.15");
      const distance = rect.top + rect.height / 2 - viewportCenter;
      const offset = Math.max(-110, Math.min(110, distance * strength * -0.18));
      item.style.setProperty("--sa-v6-depth-y", `${offset.toFixed(2)}px`);
    });

    typeScrollItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -180 || rect.top > window.innerHeight + 180) return;
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      const direction = Number.parseFloat(item.dataset.saV6TypeDirection || "1");
      const travel = Math.min(window.innerWidth * 0.24, 360);
      const offset = (0.5 - progress) * travel * direction;
      item.style.setProperty("--sa-v6-type-x", `${offset.toFixed(2)}px`);
    });

    sequenceStates.forEach((state) => {
      if (!state.near || state.isStatic) return;
      const progressElement = state.progressElement || state.element;
      const rect = progressElement.getBoundingClientRect();
      const hasExternalProgressRoot = progressElement !== state.element;
      const travel = hasExternalProgressRoot
        ? Math.max(window.innerHeight * 0.55, rect.height - window.innerHeight * 0.25)
        : Math.max(1, rect.height - window.innerHeight);
      const progress = hasExternalProgressRoot
        ? Math.max(0, Math.min(1, (window.innerHeight * 0.12 - rect.top) / travel))
        : Math.max(0, Math.min(1, -rect.top / travel));
      const frameIndex = Math.round(progress * (state.urls.length - 1));
      state.showFrame(frameIndex);
    });

    lyricStates.forEach((state) => {
      const rect = state.element.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > window.innerHeight + 120) return;
      const start = window.innerHeight * 0.84;
      const travel = Math.max(window.innerHeight * 0.54, rect.height * 1.35);
      const progress = Math.max(0, Math.min(1, (start - rect.top) / travel));
      const litWords = Math.round(progress * (state.words.length + 2));
      if (litWords === state.litWords) return;
      state.litWords = litWords;
      state.words.forEach((word, index) => word.classList.toggle("is-lit", index < litWords));
    });

    processStates.forEach((state) => {
      const rootRect = state.element.getBoundingClientRect();
      if (rootRect.bottom < -120 || rootRect.top > window.innerHeight + 120) {
        state.pauseVideos();
        return;
      }

      const centers = state.steps.map((step) => {
        const rect = step.getBoundingClientRect();
        return rect.top + rect.height / 2;
      });
      if (!centers.length) return;

      const anchor = window.innerHeight * (window.innerWidth < 750 ? 0.7 : 0.52);
      let indexFloat = 0;
      const lastIndex = centers.length - 1;

      if (anchor >= centers[lastIndex]) {
        indexFloat = lastIndex;
      } else if (anchor > centers[0]) {
        for (let index = 0; index < lastIndex; index += 1) {
          if (anchor > centers[index + 1]) continue;
          const distance = Math.max(1, centers[index + 1] - centers[index]);
          indexFloat = index + (anchor - centers[index]) / distance;
          break;
        }
      }

      state.render(indexFloat);
    });
  };

  const requestParallax = () => {
    if (parallaxFrame) return;
    parallaxFrame = window.requestAnimationFrame(paintParallax);
  };

  const initGlass = (scope = document) => {
    scope.querySelectorAll(glassSelector).forEach((glass) => {
      if (glass.dataset.saV6GlassReady) return;
      glass.dataset.saV6GlassReady = "true";

      glass.addEventListener(
        "pointermove",
        (event) => {
          if (reducedMotion.matches) return;
          const rect = glass.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          glass.style.setProperty("--sa-v6-glass-x", `${x.toFixed(1)}%`);
          glass.style.setProperty("--sa-v6-glass-y", `${y.toFixed(1)}%`);
        },
        { passive: true }
      );
    });
  };

  const initProcess = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-process]").forEach((process) => {
      if (process.dataset.saV6ProcessReady) return;
      process.dataset.saV6ProcessReady = "true";
      const steps = [...process.querySelectorAll("[data-sa-v6-process-step]")];
      const media = [...process.querySelectorAll("[data-sa-v6-process-media]")];
      const titles = [...process.querySelectorAll("[data-sa-v6-process-title]")];
      if (!steps.length) return;

      const state = {
        element: process,
        steps,
        media,
        titles,
        activeIndex: -1,
        pauseVideos: () => {
          media.forEach((item) => item.querySelector("video")?.pause());
        },
        render: () => {}
      };

      state.render = (indexFloat) => {
        const boundedIndex = Math.max(0, Math.min(steps.length - 1, indexFloat));
        const activeIndex = Math.round(boundedIndex);

        media.forEach((item, mediaIndex) => {
          const distance = Math.abs(boundedIndex - mediaIndex);
          const opacity = Math.max(0, 1 - distance);
          const scale = 1 + Math.min(1, distance) * 0.028;
          item.style.setProperty("--sa-v9-process-opacity", opacity.toFixed(3));
          item.style.setProperty("--sa-v9-process-scale", scale.toFixed(3));
          const isActive = mediaIndex === activeIndex;
          item.classList.toggle("is-active", isActive);
        });

        if (state.activeIndex === activeIndex) return;
        state.activeIndex = activeIndex;
        steps.forEach((step, stepIndex) => step.classList.toggle("is-active", stepIndex === activeIndex));
        titles.forEach((title, titleIndex) => title.classList.toggle("is-active", titleIndex === activeIndex));
        media.forEach((item, mediaIndex) => {
          const video = item.querySelector("video");
          if (!video) return;
          if (mediaIndex === activeIndex && !reducedMotion.matches) {
            const playPromise = video.play();
            playPromise?.catch?.(() => {});
          } else {
            video.pause();
          }
        });
      };

      state.render(0);

      if (reducedMotion.matches) {
        state.pauseVideos();
        return;
      }

      processStates.push(state);
    });
  };

  const initAnchors = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-anchor]").forEach((anchor) => {
      if (anchor.dataset.saV6AnchorReady) return;
      anchor.dataset.saV6AnchorReady = "true";
      anchor.addEventListener("click", (event) => {
        const hash = anchor.hash;
        if (!hash) return;
        const target = document.querySelector(hash);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "start" });
        window.history.replaceState(null, "", hash);
      });
    });
  };

  const initPointerLight = () => {
    if (!document.querySelector("[data-sa-v6-home]") || reducedMotion.matches) return;
    window.addEventListener(
      "pointermove",
      (event) => {
        root.style.setProperty("--sa-v6-pointer-x", `${event.clientX}px`);
        root.style.setProperty("--sa-v6-pointer-y", `${event.clientY}px`);
      },
      { passive: true }
    );
  };

  const initLyrics = (scope = document) => {
    scope.querySelectorAll("[data-sa-v8-lyrics]").forEach((element) => {
      if (element.dataset.saV8LyricsReady) return;
      element.dataset.saV8LyricsReady = "true";
      const words = element.textContent.trim().split(/\s+/).filter(Boolean);
      if (!words.length) return;
      element.textContent = "";
      const wordElements = words.map((word, index) => {
        const span = document.createElement("span");
        span.className = "sa-v8-lyrics__word";
        span.textContent = word;
        element.append(span);
        if (index < words.length - 1) element.append(document.createTextNode(" "));
        return span;
      });
      const state = { element, words: wordElements, litWords: -1 };
      lyricStates.push(state);
      if (reducedMotion.matches) wordElements.forEach((word) => word.classList.add("is-lit"));
    });
  };

  const initLogoMelt = (scope = document) => {
    scope.querySelectorAll("[data-sa-v8-logo]").forEach((logo) => {
      if (logo.dataset.saV8LogoReady) return;
      logo.dataset.saV8LogoReady = "true";
      const frame = logo.querySelector("[data-sa-v8-logo-frame]");
      const manifest = logo.querySelector("[data-sa-v8-logo-manifest]");
      const header = logo.closest(".header-wrapper");
      const staticLogo = window.matchMedia("(max-width: 749px), (hover: none), (pointer: coarse)").matches;
      if (!frame || !manifest || !header || reducedMotion.matches || saveData || staticLogo) return;

      let urls = [];
      try {
        urls = JSON.parse(manifest.textContent);
      } catch (error) {
        return;
      }
      if (!urls.length) return;

      const images = new Map();
      let animationFrame = 0;
      let currentIndex = 0;
      let targetIndex = 0;
      let preloadRequested = false;
      let idleScheduled = false;
      let idleReady = false;
      let nextImage = 0;
      let activeLoads = 0;
      let readyImages = 0;
      let loadFailed = false;
      const lastIndex = urls.length - 1;
      const coarsePointer = window.matchMedia("(hover: none), (pointer: coarse)").matches;
      const concurrency = coarsePointer ? 2 : 3;
      const canRun = () => logo.isConnected && !document.hidden && !reducedMotion.matches &&
        !body.classList.contains("sa-mobile-menu-open");

      const stopAnimation = () => {
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      };
      const showFrame = (index) => {
        const nextIndex = Math.max(0, Math.min(lastIndex, index));
        if (nextIndex === currentIndex) return;
        currentIndex = nextIndex;
        frame.src = urls[currentIndex];
      };

      const animateTo = (destination) => {
        if (!canRun() || readyImages !== urls.length || loadFailed) return;
        if (destination === currentIndex && !animationFrame) return;
        stopAnimation();

        const fromIndex = currentIndex;
        const distance = Math.abs(destination - fromIndex) / Math.max(1, lastIndex);
        const duration = Math.max(240, (destination === 0 ? 1600 : 3000) * distance);
        logo.classList.add("is-melting");
        logo.classList.toggle("is-reversing", destination < fromIndex);
        logo.classList.remove("is-melted");
        const startedAt = performance.now();

        const tick = (timestamp) => {
          if (!canRun()) {
            animationFrame = 0;
            return;
          }
          const rawProgress = Math.max(0, Math.min(1, (timestamp - startedAt) / duration));
          const progress = 1 - Math.pow(1 - rawProgress, 3);
          showFrame(Math.round(fromIndex + (destination - fromIndex) * progress));
          if (progress < 1) {
            animationFrame = window.requestAnimationFrame(tick);
            return;
          }

          animationFrame = 0;
          showFrame(destination);
          logo.classList.remove("is-reversing");
          if (destination === 0) {
            logo.classList.remove("is-melting", "is-melted");
          } else if (destination === lastIndex) {
            logo.classList.add("is-melted");
          }
        };

        animationFrame = window.requestAnimationFrame(tick);
      };

      // The logo is decorative: let the storefront finish its initial work first.
      // A small queue avoids 100 competing requests on mobile connections.
      const pumpImages = () => {
        if (!idleReady || !preloadRequested || !canRun() || loadFailed) return;
        while (activeLoads < concurrency && nextImage < urls.length) {
          const index = nextImage++;
          const image = new Image();
          image.decoding = "async";
          image.fetchPriority = "low";
          images.set(index, image);
          activeLoads += 1;
          let settled = false;
          const finish = (success) => {
            if (settled) return;
            settled = true;
            activeLoads -= 1;
            if (success) readyImages += 1;
            else loadFailed = true;
            pumpImages();
            if (readyImages === urls.length) animateTo(targetIndex);
          };
          image.onload = () => {
            if (typeof image.decode === "function") {
              image.decode().then(() => finish(true), () => finish(false));
            } else {
              finish(Boolean(image.naturalWidth));
            }
          };
          image.onerror = () => finish(false);
          image.src = urls[index];
        }
      };

      const schedulePreload = () => {
        if (idleReady) {
          pumpImages();
          return;
        }
        if (idleScheduled) return;
        idleScheduled = true;
        const afterLoad = () => {
          const allowPreload = () => {
            idleReady = true;
            pumpImages();
          };
          if ("requestIdleCallback" in window) {
            window.requestIdleCallback(allowPreload, { timeout: 2000 });
          } else {
            window.setTimeout(allowPreload, 250);
          }
        };
        if (document.readyState === "complete") afterLoad();
        else window.addEventListener("load", afterLoad, { once: true });
      };

      const start = () => {
        if (reducedMotion.matches) return;
        targetIndex = lastIndex;
        preloadRequested = true;
        schedulePreload();
        animateTo(targetIndex);
      };
      const restore = () => {
        // Leaving while the queue is pending must not start an old hover later.
        targetIndex = 0;
        preloadRequested = false;
        animateTo(0);
      };
      const resume = () => {
        if (reducedMotion.matches) {
          targetIndex = 0;
          preloadRequested = false;
          stopAnimation();
          showFrame(0);
          logo.classList.remove("is-melting", "is-melted", "is-reversing");
          return;
        }
        if (!canRun()) {
          stopAnimation();
          return;
        }
        pumpImages();
        if (!animationFrame) animateTo(targetIndex);
      };

      if (!coarsePointer) {
        header.addEventListener("pointerenter", () => {
          preloadRequested = true;
          schedulePreload();
        }, { passive: true });
        header.addEventListener("pointerleave", restore, { passive: true });
      }
      logo.addEventListener("pointerenter", start, { passive: true });
      document.addEventListener("visibilitychange", resume);
      reducedMotion.addEventListener?.("change", resume);
      const menuObserver = new MutationObserver(resume);
      menuObserver.observe(body, { attributes: true, attributeFilter: ["class"] });
    });
  };

  const initMeltText = (scope = document) => {
    const items = [...scope.querySelectorAll("[data-sa-v9-melt-text]")].filter(
      (item) => !item.dataset.saV9MeltReady
    );
    if (!items.length) return;

    if (reducedMotion.matches || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.dataset.saV9MeltReady = "true";
        item.classList.add("is-melted");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-melted");
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -22% 0px", threshold: 0.35 }
    );

    items.forEach((item) => {
      item.dataset.saV9MeltReady = "true";
      observer.observe(item);
    });
  };

  const initSequences = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-sequence]").forEach((element) => {
      if (element.dataset.saV6SequenceReady) return;
      element.dataset.saV6SequenceReady = "true";

      const canvas = element.querySelector("[data-sa-v6-sequence-canvas]");
      const manifest = element.querySelector("[data-sa-v6-sequence-manifest]");
      if (!canvas || !manifest) return;

      let urls = [];
      try {
        urls = JSON.parse(manifest.textContent);
      } catch (error) {
        element.classList.add("is-static");
        return;
      }

      if (!urls.length) {
        element.classList.add("is-static");
        return;
      }

      const context = canvas.getContext("2d", { alpha: false });
      const state = {
        element,
        progressElement: element.dataset.saV6SequenceProgressRoot
          ? element.closest(element.dataset.saV6SequenceProgressRoot)
          : element,
        canvas,
        context,
        urls,
        images: new Map(),
        currentFrame: -1,
        near: !("IntersectionObserver" in window),
        isStatic: reducedMotion.matches || saveData || !context,
        showFrame: () => {}
      };

      const loadFrame = (frameIndex) => {
        if (frameIndex < 0 || frameIndex >= state.urls.length || state.images.has(frameIndex)) return;
        const image = new Image();
        image.decoding = "async";
        state.images.set(frameIndex, image);
        image.addEventListener(
          "load",
          () => {
            if (state.currentFrame !== frameIndex || !state.context) return;
            state.context.drawImage(image, 0, 0, state.canvas.width, state.canvas.height);
            state.element.classList.add("is-ready");
          },
          { once: true }
        );
        image.src = state.urls[frameIndex];
      };

      state.showFrame = (frameIndex) => {
        if (frameIndex === state.currentFrame) return;
        state.currentFrame = frameIndex;
        state.element.dataset.saV6SequenceFrame = String(frameIndex);

        for (let offset = -3; offset <= 3; offset += 1) loadFrame(frameIndex + offset);

        const currentImage = state.images.get(frameIndex);
        if (currentImage?.complete && currentImage.naturalWidth && state.context) {
          state.context.drawImage(currentImage, 0, 0, state.canvas.width, state.canvas.height);
          state.element.classList.add("is-ready");
        }

        state.images.forEach((image, cachedIndex) => {
          if (Math.abs(cachedIndex - frameIndex) <= 5) return;
          state.images.delete(cachedIndex);
          image.src = "";
        });
      };

      if (state.isStatic) {
        element.classList.add("is-static");
      } else {
        sequenceStates.push(state);
        if ("IntersectionObserver" in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                state.near = entry.isIntersecting;
                if (state.near) {
                  state.showFrame(Math.max(0, state.currentFrame));
                  requestParallax();
                }
              });
            },
            { rootMargin: "120% 0px 120% 0px" }
          );
          observer.observe(element);
        }
      }
    });
  };

  const initReviews = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-reviews]").forEach((carousel) => {
      if (carousel.dataset.saV6ReviewsReady) return;
      carousel.dataset.saV6ReviewsReady = "true";

      const track = carousel.querySelector("[data-sa-v6-reviews-track]");
      const cards = track ? [...track.children] : [];
      const previous = carousel.querySelector("[data-sa-v6-reviews-prev]");
      const next = carousel.querySelector("[data-sa-v6-reviews-next]");
      const status = carousel.querySelector("[data-sa-v6-reviews-status]");
      if (!track || !cards.length || !previous || !next || !status) return;

      let activeIndex = 0;
      const updateStatus = () => {
        status.textContent = `${activeIndex + 1} / ${cards.length}`;
        previous.disabled = activeIndex === 0;
        next.disabled = activeIndex === cards.length - 1;
      };
      const goTo = (nextIndex) => {
        activeIndex = Math.max(0, Math.min(cards.length - 1, nextIndex));
        track.scrollTo({
          left: Math.max(0, cards[activeIndex].offsetLeft - track.offsetLeft - 12),
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
        updateStatus();
      };

      previous.addEventListener("click", () => goTo(activeIndex - 1));
      next.addEventListener("click", () => goTo(activeIndex + 1));
      track.addEventListener(
        "scroll",
        () => {
          const trackLeft = track.getBoundingClientRect().left;
          activeIndex = cards.reduce((closest, card, index) => {
            const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
            return distance < closest.distance ? { index, distance } : closest;
          }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
          updateStatus();
        },
        { passive: true }
      );
      track.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        if (event.key === "ArrowLeft") goTo(activeIndex - 1);
        if (event.key === "ArrowRight") goTo(activeIndex + 1);
        if (event.key === "Home") goTo(0);
        if (event.key === "End") goTo(cards.length - 1);
      });

      updateStatus();
    });
  };

  const initHomeProof = (scope = document) => {
    scope.querySelectorAll("[data-sa-v17-home-proof]").forEach((carousel) => {
      if (carousel.dataset.saV17HomeProofReady) return;

      const slides = [...carousel.querySelectorAll("[data-sa-v17-home-proof-slide]")];
      const progress = carousel.querySelector("[data-sa-v17-home-proof-progress]");
      if (slides.length < 2 || !progress) return;

      carousel.dataset.saV17HomeProofReady = "true";
      let activeIndex = Math.max(
        0,
        slides.findIndex((slide) => slide.classList.contains("is-active"))
      );
      let timer = 0;
      let isVisible = !("IntersectionObserver" in window);
      let isHovered = false;
      let hasFocus = carousel.contains(document.activeElement);

      const clearTimer = () => {
        if (!timer) return;
        window.clearInterval(timer);
        timer = 0;
      };

      const restartProgress = () => {
        carousel.classList.remove("is-progressing");
        void carousel.offsetWidth;
        carousel.classList.add("is-progressing");
      };

      const show = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle("is-active", isActive);
          slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        });
      };

      const start = () => {
        clearTimer();
        carousel.classList.remove("is-progressing");
        if (reducedMotion.matches || document.hidden || !isVisible || isHovered || hasFocus) return;

        restartProgress();
        timer = window.setInterval(() => {
          show(activeIndex + 1);
          restartProgress();
        }, 3000);
      };

      carousel.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "touch") return;
        isHovered = true;
        start();
      });
      carousel.addEventListener("pointerleave", (event) => {
        if (event.pointerType === "touch") return;
        isHovered = false;
        start();
      });
      carousel.addEventListener("focusin", () => {
        hasFocus = true;
        start();
      });
      carousel.addEventListener("focusout", (event) => {
        hasFocus = carousel.contains(event.relatedTarget);
        start();
      });

      document.addEventListener("visibilitychange", start);
      reducedMotion.addEventListener?.("change", start);

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            isVisible = entry.isIntersecting;
            start();
          },
          { threshold: 0.2 }
        );
        observer.observe(carousel);
      } else {
        start();
      }
    });
  };

  const initMobileHeaderMenu = () => {
    const HeaderDrawer = window.customElements?.get("header-drawer");
    if (!HeaderDrawer) {
      window.customElements?.whenDefined("header-drawer").then(initMobileHeaderMenu);
      return;
    }

    const prototype = HeaderDrawer.prototype;
    if (prototype.saMobileMenuEnhanced) return;

    Object.defineProperty(prototype, "saMobileMenuEnhanced", {
      configurable: true,
      value: true,
    });

    const mobileViewport = window.matchMedia("(max-width: 989px)");
    const originalOpenMenuDrawer = prototype.openMenuDrawer;
    const originalCloseMenuDrawer = prototype.closeMenuDrawer;

    const restoreAttribute = (element, name, value) => {
      if (!element) return;
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    };

    const getState = (drawer) => {
      if (drawer.saMobileMenuState) return drawer.saMobileMenuState;

      const wrapper = drawer.closest(".header-wrapper");
      const headerSection = wrapper?.closest(".section-header");
      const header = wrapper?.querySelector(".header");
      const panel = drawer.querySelector("#menu-drawer");
      let surface = headerSection?.querySelector(".sa-mobile-menu-surface");

      if (headerSection && !surface) {
        surface = document.createElement("div");
        surface.className = "sa-mobile-menu-surface";
        surface.setAttribute("aria-hidden", "true");
        headerSection.prepend(surface);
      }

      drawer.saMobileMenuState = {
        active: false,
        closing: false,
        drawer,
        wrapper,
        headerSection,
        header,
        panel,
        surface,
        summary: drawer.mainDetailsToggle?.querySelector("summary"),
        scrollY: 0,
        viewportWidth: window.innerWidth,
        pageLocked: false,
        closeEvent: null,
        closeFocus: null,
        motionPhase: false,
        inertTargets: [],
        afterClose: null,
        panelRole: panel?.getAttribute("role") ?? null,
        panelAriaModal: panel?.getAttribute("aria-modal") ?? null,
        panelAriaLabel: panel?.getAttribute("aria-label") ?? null,
        summaryAriaLabel: drawer.mainDetailsToggle?.querySelector("summary")?.getAttribute("aria-label") ?? null,
      };

      return drawer.saMobileMenuState;
    };

    const setGeometry = (state) => {
      if (!state.wrapper || !state.headerSection || !state.header) return;

      const wrapperRect = state.wrapper.getBoundingClientRect();
      const visibleHeaderLeft = Math.max(
        0,
        Math.min(wrapperRect.left, window.innerWidth - wrapperRect.width)
      );
      const visibleHeaderTop = Math.max(
        0,
        Math.min(wrapperRect.top, window.innerHeight - wrapperRect.height)
      );
      const style = state.headerSection.style;

      style.setProperty("--sa-menu-header-top", `${visibleHeaderTop}px`);
      style.setProperty("--sa-menu-header-left", `${visibleHeaderLeft}px`);
      style.setProperty("--sa-menu-header-width", `${wrapperRect.width}px`);
      style.setProperty("--sa-menu-header-height", `${wrapperRect.height}px`);
      style.setProperty("--sa-menu-drawer-top", `${visibleHeaderTop + wrapperRect.height + 8}px`);
    };

    const setBackgroundInert = (state, inert) => {
      if (inert) {
        state.inertTargets = [...document.body.children]
          .filter(
            (element) =>
              element !== state.headerSection &&
              !["SCRIPT", "STYLE", "LINK", "TEMPLATE"].includes(element.tagName)
          )
          .map((element) => ({ element, wasInert: element.inert }));
        state.inertTargets.forEach(({ element }) => {
          element.inert = true;
        });
        return;
      }

      state.inertTargets.forEach(({ element, wasInert }) => {
        if (element.isConnected) element.inert = wasInert;
      });
      state.inertTargets = [];
    };

    const lockPage = (state, scrollPosition = window.scrollY) => {
      if (state.pageLocked) return;
      state.scrollY = scrollPosition;
      state.viewportWidth = window.innerWidth;
      state.pageLocked = true;
      processStates.forEach((processState) => processState.pauseVideos());
      document.documentElement.style.setProperty("--sa-menu-lock-top", `${-state.scrollY}px`);
      document.documentElement.classList.add("sa-mobile-menu-locked");
      document.body.classList.add("sa-mobile-menu-open");
      setBackgroundInert(state, true);
    };

    const unlockPage = (state) => {
      if (!state.pageLocked) return;
      const root = document.documentElement;
      const inlineScrollBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      setBackgroundInert(state, false);
      document.body.classList.remove(
        "sa-mobile-menu-open",
        "sa-mobile-menu-expanded",
        "sa-mobile-menu-closing"
      );
      root.classList.remove("sa-mobile-menu-locked");
      root.style.removeProperty("--sa-menu-lock-top");
      window.scrollTo(0, state.scrollY);
      if (inlineScrollBehavior) root.style.scrollBehavior = inlineScrollBehavior;
      else root.style.removeProperty("scroll-behavior");
      state.pageLocked = false;
      requestParallax();
    };

    const clearPanelDialogState = (state) => {
      restoreAttribute(state.panel, "role", state.panelRole);
      restoreAttribute(state.panel, "aria-modal", state.panelAriaModal);
      restoreAttribute(state.panel, "aria-label", state.panelAriaLabel);
    };

    const clearEntryMotion = (state) => {
      state.headerSection?.classList.remove(
        "sa-mobile-menu-motion-a",
        "sa-mobile-menu-motion-b"
      );
    };

    const startEntryMotion = (state) => {
      clearEntryMotion(state);
      if (reducedMotion.matches || saveData) return;
      state.motionPhase = !state.motionPhase;
      state.headerSection?.classList.add(
        state.motionPhase ? "sa-mobile-menu-motion-a" : "sa-mobile-menu-motion-b"
      );
    };

    const runAfterClose = (state) => {
      const callback = state.afterClose;
      state.afterClose = null;
      if (typeof callback === "function") window.requestAnimationFrame(callback);
    };

    const cleanup = (state) => {
      document.removeEventListener("keydown", state.onKeydown, true);
      window.removeEventListener("resize", state.onViewportResize);
      state.panel?.removeEventListener("click", state.onPanelClick);
      state.wrapper?.removeEventListener("click", state.onHeaderAction, true);
      clearEntryMotion(state);
      clearPanelDialogState(state);
      restoreAttribute(state.summary, "aria-label", state.summaryAriaLabel);
      unlockPage(state);
      state.headerSection?.classList.remove("sa-mobile-menu-active");
      [
        "--sa-menu-header-top",
        "--sa-menu-header-left",
        "--sa-menu-header-width",
        "--sa-menu-header-height",
        "--sa-menu-drawer-top",
      ].forEach((property) => state.headerSection?.style.removeProperty(property));
      state.active = false;
      state.closing = false;
      state.closeEvent = null;
      state.closeFocus = null;
      runAfterClose(state);
    };

    const finishClose = (state) => {
      if (!state.active) return;
      const hadOwnCloseAnimation = Object.prototype.hasOwnProperty.call(
        state.drawer,
        "closeAnimation"
      );
      const savedCloseAnimation = state.drawer.closeAnimation;
      state.drawer.closeAnimation = (detailsElement) => {
        detailsElement?.removeAttribute("open");
      };
      try {
        originalCloseMenuDrawer.call(
          state.drawer,
          state.closeEvent || new Event("sa-mobile-menu-close"),
          state.closeFocus || state.summary
        );
      } finally {
        if (hadOwnCloseAnimation) state.drawer.closeAnimation = savedCloseAnimation;
        else delete state.drawer.closeAnimation;
      }
      cleanup(state);
    };

    const requestClose = (state, event, elementToFocus) => {
      if (!state.active || state.closing) return;
      clearEntryMotion(state);
      state.closing = true;
      state.closeEvent = event || new Event("sa-mobile-menu-close");
      state.closeFocus = elementToFocus || state.summary;
      state.summary?.setAttribute("aria-expanded", "false");
      document.body.classList.add("sa-mobile-menu-closing");
      unlockPage(state);
      finishClose(state);
    };

    const openEnhancedMenu = (drawer, summaryElement) => {
      const state = getState(drawer);
      if (!state.wrapper || !state.headerSection || !state.panel || state.active) return;

      const openingScrollY = window.scrollY;
      setGeometry(state);
      state.active = true;
      state.closing = false;
      state.summary = summaryElement;
      summaryElement.setAttribute(
        "aria-label",
        document.documentElement.lang.toLowerCase().startsWith("cs") ? "Zavřít nabídku" : document.documentElement.lang.toLowerCase().startsWith("es") ? "Cerrar menú" : "Close menu"
      );
      state.headerSection.classList.add("sa-mobile-menu-active");
      lockPage(state, openingScrollY);
      startEntryMotion(state);
      drawer.header = drawer.header || state.headerSection;
      drawer.header.classList.add("menu-open");
      drawer.mainDetailsToggle.classList.add("menu-opening");
      summaryElement.setAttribute("aria-expanded", "true");
      if (typeof trapFocus === "function") trapFocus(drawer.mainDetailsToggle, summaryElement);

      state.panel.setAttribute("role", "dialog");
      state.panel.setAttribute("aria-modal", "true");
      state.panel.setAttribute("aria-label", state.summaryAriaLabel || "Menu");

      state.onKeydown = (event) => {
        if (event.key !== "Escape" || !state.active) return;
        const expandedLocalization = state.panel.querySelector(
          ".menu-drawer__localization [aria-expanded='true']"
        );
        if (expandedLocalization) {
          event.preventDefault();
          event.stopImmediatePropagation();
          expandedLocalization.click();
          return;
        }

        const openSubmenu = state.panel.querySelector(".menu-drawer__menu details[open]");
        if (openSubmenu) {
          event.preventDefault();
          event.stopImmediatePropagation();
          openSubmenu.querySelector("summary")?.click();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        requestClose(state, event, state.summary);
      };

      state.onViewportResize = () => {
        if (!mobileViewport.matches || Math.abs(window.innerWidth - state.viewportWidth) > 2) {
          requestClose(state, new Event("resize"), state.summary);
        }
      };

      state.onPanelClick = (event) => {
        const link = event.target.closest("a[href]");
        if (!link) return;

        let url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (error) {
          return;
        }

        const isSamePageAnchor =
          url.origin === window.location.origin &&
          url.pathname === window.location.pathname &&
          url.search === window.location.search &&
          Boolean(url.hash);
        if (!isSamePageAnchor) return;

        let target;
        try {
          target = document.querySelector(url.hash);
        } catch (error) {
          return;
        }
        if (!target) return;

        event.preventDefault();
        state.afterClose = () => {
          target.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "start" });
          window.history?.pushState?.(null, "", url.hash);
        };
        requestClose(state, event, state.summary);
      };

      state.onHeaderAction = (event) => {
        if (!state.active) return;
        const searchToggle = event.target.closest(".header__search summary");
        const cartLink = event.target.closest("#cart-icon-bubble");
        if (!searchToggle && !cartLink) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        state.afterClose = () => {
          if (cartLink) {
            window.location.assign(cartLink.href);
            return;
          }
          searchToggle.click();
        };
        requestClose(state, event, state.summary);
      };

      document.addEventListener("keydown", state.onKeydown, true);
      window.addEventListener("resize", state.onViewportResize, { passive: true });
      state.panel.addEventListener("click", state.onPanelClick);
      state.wrapper.addEventListener("click", state.onHeaderAction, true);

    };

    prototype.openMenuDrawer = function (summaryElement) {
      if (!mobileViewport.matches || !document.body.classList.contains("sa-com-v6")) {
        return originalOpenMenuDrawer.call(this, summaryElement);
      }
      return openEnhancedMenu(this, summaryElement);
    };

    prototype.closeMenuDrawer = function (event, elementToFocus) {
      const state = this.saMobileMenuState;
      if (!state?.active || !mobileViewport.matches || !elementToFocus) {
        return originalCloseMenuDrawer.call(this, event, elementToFocus);
      }
      return requestClose(state, event, elementToFocus);
    };

    document.querySelectorAll("header-drawer").forEach((drawer) => getState(drawer));
  };

  const init = (scope = document) => {
    initMobileHeaderMenu();
    initReveal(scope);
    initGlass(scope);
    initLyrics(scope);
    initLogoMelt(scope);
    initMeltText(scope);
    initProcess(scope);
    initAnchors(scope);
    initSequences(scope);
    initReviews(scope);
    initHomeProof(scope);
    refreshDepthItems();
    requestParallax();
  };

  init();
  initPointerLight();

  window.addEventListener("scroll", requestParallax, { passive: true });
  window.addEventListener("resize", requestParallax, { passive: true });

  reducedMotion.addEventListener?.("change", () => {
    if (reducedMotion.matches) revealAll();
    sequenceStates.forEach((state) => {
      state.isStatic = reducedMotion.matches || saveData;
      state.element.classList.toggle("is-static", state.isStatic);
    });
    requestParallax();
  });

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();

